import React from 'react';
import { Box, Typography, Chip } from '@mui/material';

export const JOURS_EDT = [
  { bit: 1, nom: 'lundi' },
  { bit: 2, nom: 'mardi' },
  { bit: 4, nom: 'mercredi' },
  { bit: 8, nom: 'jeudi' },
  { bit: 16, nom: 'vendredi' },
  { bit: 32, nom: 'samedi' }
];

export const JOURS_EDT_TA = [
  ...JOURS_EDT,
  { bit: 64, nom: 'dimanche' }
];

export const CRENEAUX_EDT = [
  { index: 0, label: '09:00 - 10:30', debut: 9 * 60 },
  { index: 1, label: '10:45 - 12:15', debut: 10 * 60 + 45 },
  { index: 2, label: '13:15 - 14:45', debut: 13 * 60 + 15 },
  { index: 3, label: '15:00 - 16:30', debut: 15 * 60 }
];

export const CRENEAUX_EDT_TA = [
  { index: 0, label: '19:00 - 20:30', debut: 19 * 60 },
  { index: 1, label: '20:30 - 22:00', debut: 20 * 60 + 30 },
  { index: 2, label: '09:30 - 11:00', debut: 9 * 60 + 30 },
  { index: 3, label: '11:00 - 12:30', debut: 11 * 60 },
  { index: 4, label: '13:30 - 15:00', debut: 13 * 60 + 30 },
  { index: 5, label: '15:00 - 16:30', debut: 15 * 60 }
];

export const DUREE_CRENEAU = 90; // minutes par creneau de 1h30
export const FIN_APRES_MIDI = 16 * 60 + 30; // 16:30, fin de la journee de cours
export const FIN_TA_SOIR = 22 * 60;
export const FIN_TA_WEEKEND = 16 * 60 + 30;

const DUREE_JOUR = 90;

export const normaliserRegime = (r) => (String(r || '').toUpperCase() === 'TA' ? 'TA' : 'NORMAL');

export const grilleDe = (regime) => (
  normaliserRegime(regime) === 'TA'
    ? { jours: JOURS_EDT_TA, creneaux: CRENEAUX_EDT_TA, finJournee: FIN_TA_SOIR }
    : { jours: JOURS_EDT, creneaux: CRENEAUX_EDT, finJournee: FIN_APRES_MIDI }
);

const versHeure = (minutes) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// "creneau 0 sur 2 creneaux" -> "09:00 - 12:15"
// la fin se calcule sur le dernier creneau reellement occupe, sinon on afficherait 12:00
export const plageHoraire = (creneau, duree = 1, regime = 'NORMAL') => {
  const creneaux = grilleDe(regime).creneaux;
  const c = creneaux.find(x => x.index === Number(creneau));
  if (!c) return '';
  const n = Math.max(1, Math.min(Number(duree) || 1, creneaux.length - c.index));
  const dernier = creneaux[c.index + n - 1];
  return `${versHeure(c.debut)} - ${versHeure(dernier.debut + DUREE_JOUR)}`;
};

const COULEURS = [
  { bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '#c7d2fe', titre: '#312e81' },
  { bg: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)', border: '#a5f3fc', titre: '#155e75' },
  { bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '#bbf7d0', titre: '#14532d' },
  { bg: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', border: '#fed7aa', titre: '#7c2d12' }
];

// JS : 0 = dimanche ... 6 = samedi. Renvoie le nom de jour du modele EDT.
export const jourEDT = (date, regime = 'NORMAL') => {
  const n = date.getDay();
  const jours = grilleDe(regime).jours;
  const bit = n === 0 ? 64 : (1 << (n - 1));
  return jours.find(j => j.bit === bit)?.nom || null;
};

export const GrilleEmploiSemaine = ({ seances = [], showGroupes = false, regime = 'NORMAL', vide = 'Aucun créneau dans votre emploi du temps' }) => {
  const grille = grilleDe(regime);
  const regimeGrille = normaliserRegime(regime);
  const seants = seances.filter(s => Number(s.creneau) >= 0
    && Number(s.creneau) < grille.creneaux.length
    && normaliserRegime(s.regime) === regimeGrille);

  if (seants.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">{vide}</Typography>
      </Box>
    );
  }

  const joursAffiches = regimeGrille === 'TA' ? grille.jours : grille.jours.filter(j => j.bit <= 16);
  const aLaSeance = (nom, creneau) => seants.find(s => s.jour === nom && Number(s.creneau) === creneau);

  // une seance fusionnee occupe le creneau suivant : cette colonne-la est masquee
  const estCouvert = (nom, creneau) => {
    const precedente = aLaSeance(nom, creneau - 1);
    return !!(precedente && (Number(precedente.duree) || 1) > 1);
  };

  // une seance ne peut pas deborder sur le creneau suivant
  const portee = (s, creneau) => Math.max(1, Math.min(Number(s.duree) || 1, grille.creneaux.length - creneau));

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px', tableLayout: 'fixed' }}>
        <Box component="thead">
          <Box component="tr" sx={{ background: '#f9fafb' }}>
            <Box component="th" sx={{ py: 1.5, px: 1, width: 105, textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: '0.75rem', borderBottom: '2px solid #e5e7eb' }}>
              Jour
            </Box>
            {grille.creneaux.map(h => (
              <Box component="th" key={h.index} sx={{ py: 1.5, px: 1, textAlign: 'center', fontWeight: 700, color: '#4f46e5', fontSize: '0.75rem', borderBottom: '2px solid #e5e7eb' }}>
                {h.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {joursAffiches.map((j, indexJour) => (
            <Box component="tr" key={j.nom} sx={{ borderBottom: '1px solid #f3f4f6' }}>
              <Box component="td" sx={{ py: 1.5, px: 1, textAlign: 'center', fontWeight: 700, color: '#334155', background: '#f8fafc', borderRight: '1px solid #e5e7eb', textTransform: 'capitalize', fontSize: '0.85rem' }}>
                {j.nom}
              </Box>

              {grille.creneaux.map(h => {
                if (estCouvert(j.nom, h.index)) return null;

                const s = aLaSeance(j.nom, h.index);
                if (!s) {
                  return (
                    <Box component="td" key={`${j.nom}-libre-${h.index}`} sx={{ p: 0.5, borderBottom: '1px solid #f8fafc' }}>
                      <Box sx={{ minHeight: 76, border: '1px dashed #e2e8f0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                      </Box>
                    </Box>
                  );
                }

                const n = portee(s, h.index);
                const fusionnee = n > 1;
                const c = COULEURS[(h.index + indexJour) % COULEURS.length];

                return (
                  <Box
                    component="td"
                    key={s.cle || `${j.nom}-${h.index}`}
                    colSpan={fusionnee ? n : undefined}
                    sx={{ p: 0.5, verticalAlign: 'top', borderBottom: '1px solid #f8fafc' }}
                  >
                    <Box sx={{ minHeight: 76, height: '100%', p: 1.25, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 0.2, background: c.bg, border: `1px solid ${c.border}`, borderRadius: '10px' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c.titre, opacity: 0.75 }}>
                        {plageHoraire(s.creneau, n, regimeGrille)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: c.titre, lineHeight: 1.25, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.matiere || '—'}
                      </Typography>
                      {(s.salle || s.professeur) && (
                        <Typography variant="caption" sx={{ color: c.titre, opacity: 0.8, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {[s.salle ? `📍 ${s.salle}` : '', s.professeur ? `👨‍🏫 ${s.professeur}` : ''].filter(Boolean).join('  ·  ')}
                        </Typography>
                      )}
                      {showGroupes && s.groupes && s.groupes.length > 0 && (
                        <Typography variant="caption" sx={{ color: c.titre, opacity: 0.8, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          🎓 {s.groupes.join(', ')}
                        </Typography>
                      )}
                      {fusionnee && (
                        <Chip label="Séances fusionnées — 3h" size="small" sx={{ mt: 0.3, height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fff', color: c.titre, border: `1px solid ${c.border}` }} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Seances du jour en cours. Apres la fin des cours (16:30 en normal, 22:00 en TA) on bascule
// sur le lendemain. Le samedi n'est pas un jour de cours normal.
export const SeancesDuJour = ({ seances = [], regime = 'NORMAL' }) => {
  const regimeJour = normaliserRegime(regime);
  const grille = grilleDe(regimeJour);
  const maintenant = new Date();
  const minutes = maintenant.getHours() * 60 + maintenant.getMinutes();
  const jourBrut = jourEDT(maintenant, regimeJour);
  const jourActuel = regimeJour === 'NORMAL' && jourBrut === 'samedi' ? null : jourBrut;
  const weekend = [0, 6].includes(maintenant.getDay());
  const finJournee = regimeJour === 'TA'
    ? (weekend ? FIN_TA_WEEKEND : FIN_TA_SOIR)
    : FIN_APRES_MIDI;
  const enFinDeJournee = minutes >= finJournee;

  const prochainJourCours = () => {
    for (let i = 1; i <= 7; i += 1) {
      const d = new Date(maintenant.getTime() + i * 24 * 3600 * 1000);
      const j = jourEDT(d, regimeJour);
      if (j && (regimeJour === 'TA' || j !== 'samedi')) return j;
    }
    return 'lundi';
  };

  let jourCible = jourActuel;
  if (!jourCible) {
    jourCible = prochainJourCours();
  } else if (enFinDeJournee) {
    jourCible = prochainJourCours();
  }

  const duJour = seances
    .filter(s => s.jour === jourCible
      && Number(s.creneau) >= 0
      && Number(s.creneau) < grille.creneaux.length
      && normaliserRegime(s.regime) === regimeJour)
    .sort((a, b) => a.creneau - b.creneau);

  const estDemain = enFinDeJournee;
  // le dimanche n est pas un jour de cours : on annonce le lundi sans parler de demain
  const libelleJour = !jourActuel
    ? `Prochaine journée · ${jourCible}`
    : `${estDemain ? 'Demain' : 'Aujourd\'hui'} · ${jourCible}`;

  const statut = (s) => {
    if (estDemain || !jourActuel) return { texte: 'À venir', fond: '#eef2ff', couleur: '#4f46e5' };
    const debut = grille.creneaux.find(c => c.index === Number(s.creneau))?.debut ?? 0;
    const fin = debut + DUREE_JOUR * (Number(s.duree) || 1);
    if (minutes < debut) return { texte: 'À venir', fond: '#eef2ff', couleur: '#4f46e5' };
    if (minutes < fin) return { texte: 'En cours', fond: '#dcfce7', couleur: '#166534' };
    return { texte: 'Terminé', fond: '#f1f5f9', couleur: '#94a3b8' };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
          {libelleJour}
        </Typography>
        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
          {duJour.length} séance{duJour.length > 1 ? 's' : ''}
        </Typography>
      </Box>
      {duJour.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 1 }}>
          Aucune séance {estDemain ? 'demain' : !jourActuel ? 'le prochain jour de cours' : "aujourd'hui"}.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {duJour.map(s => {
            const st = statut(s);
            return (
              <Box
                key={s.cle || `${s.jour}-${s.creneau}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                  borderRadius: '8px', bgcolor: st.fond, border: '1px solid #e5e7eb'
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: st.couleur, minWidth: 96, whiteSpace: 'nowrap' }}>
                  {plageHoraire(s.creneau, s.duree, regimeJour)}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }} noWrap>
                    {s.matiere || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }} noWrap>
                    {[s.salle ? `📍 ${s.salle}` : '', s.professeur || ''].filter(Boolean).join('  ·  ')}
                  </Typography>
                </Box>
                <Chip label={st.texte} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: '#fff', color: st.couleur }} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

